import React, { useState, ReactNode, useEffect } from 'react';
import { Client } from '@twilio/conversations';
import { RequestDeduper } from '../services/request-managers/request-deduper';
import { fetchAllAssignedChats } from '../services/assigned-chats/fetch-all-assigned-chats';
import { TwilioConversation } from '@app/two-way/twilio-conversation.types';

export type TwilioClientContextType = {
  twilioClient: Client;
  setTwilioClient: (twilioClient: Client) => void;
  assignedChatLoader: RequestDeduper<TwilioConversation[]>;
};

export type TwilioClientContextProviderProps = {
  children: ReactNode;
};

export const TwilioClientContext = React.createContext<
  TwilioClientContextType | undefined
>(undefined);

export function TwilioClientProvider({
  children,
}: TwilioClientContextProviderProps) {
  const [twilioClient, setTwilioClient] = useState<Client | null>(null);
  const [assignedChatLoader, setAssignedChatLoader] =
    useState<RequestDeduper<TwilioConversation[]>>(null);
  useEffect(() => {
    if (twilioClient) {
      setAssignedChatLoader(
        new RequestDeduper(() => fetchAllAssignedChats(twilioClient)),
      );
    }
  }, [twilioClient]);

  return (
    <TwilioClientContext.Provider
      value={{ twilioClient, setTwilioClient, assignedChatLoader }}
    >
      {children}
    </TwilioClientContext.Provider>
  );
}

export const useTwilioClient = () => {
  const context = React.useContext(TwilioClientContext);

  if (context === undefined) {
    throw new Error(
      `useTwilioClient must be used within a TwilioClientProvider`,
    );
  }

  return context;
};
